// Domain 03 — RouteZero Southern Regional Network (Davao City).
//
//                      ISP (AS 64500) ── WEB 203.0.113.10
//                       G0/0 .1
//                        │ 198.51.100.0/30
//                       G0/0 .2
//      SRV-DC ── G0/3  R2  HQ (Davao regional hub, AS 65010)
//   10.30.10.0/24   G0/1 .1   G0/2 .1
//                  /10.30.12.0/30  \10.30.23.0/30
//            G0/0 .2                 G0/0 .2
//              R1 ──────────────────── R3
//      Branch A   G0/2 .1  10.30.13.0/30  G0/2 .2   Branch B
//      G0/1 10.30.1.1/24                 G0/1 10.30.2.1/24
//        PC-A 10.30.1.10                   PC-B 10.30.2.10
//
// Loopbacks (router IDs): R1 1.1.1.1 · R2 2.2.2.2 · R3 3.3.3.3

import { pc, server, router, link, network } from '../../engine/net/model.js';
import { createInterface } from '../../engine/ios/switching.js';

export const ADDR = {
  R1: { 'GigabitEthernet0/0': '10.30.12.2/30', 'GigabitEthernet0/1': '10.30.1.1/24', 'GigabitEthernet0/2': '10.30.13.1/30', Loopback0: '1.1.1.1/32' },
  R2: { 'GigabitEthernet0/0': '198.51.100.2/30', 'GigabitEthernet0/1': '10.30.12.1/30', 'GigabitEthernet0/2': '10.30.23.1/30', 'GigabitEthernet0/3': '10.30.10.1/24', Loopback0: '2.2.2.2/32' },
  R3: { 'GigabitEthernet0/0': '10.30.23.2/30', 'GigabitEthernet0/1': '10.30.2.1/24', 'GigabitEthernet0/2': '10.30.13.2/30', Loopback0: '3.3.3.3/32' },
  ISP: { 'GigabitEthernet0/0': '198.51.100.1/30', 'GigabitEthernet0/1': '203.0.113.1/24' },
};
export const DESC = {
  R1: { 'GigabitEthernet0/0': 'UPLINK-HQ-R2', 'GigabitEthernet0/1': 'BRANCH-A-LAN', 'GigabitEthernet0/2': 'BACKUP-R3' },
  R2: { 'GigabitEthernet0/0': 'ISP-POP-DAVAO', 'GigabitEthernet0/1': 'BRANCH-A-R1', 'GigabitEthernet0/2': 'BRANCH-B-R3', 'GigabitEthernet0/3': 'REGIONAL-DC' },
  R3: { 'GigabitEthernet0/0': 'UPLINK-HQ-R2', 'GigabitEthernet0/1': 'BRANCH-B-LAN', 'GigabitEthernet0/2': 'BACKUP-R1' },
  ISP: { 'GigabitEthernet0/0': 'ROUTEZERO-DAVAO', 'GigabitEthernet0/1': 'CONTENT' },
};
const PREFIX_MASK = { 24: '255.255.255.0', 30: '255.255.255.252', 32: '255.255.255.255' };
const ipm = (s) => { const [ip, p] = s.split('/'); return { ip, mask: PREFIX_MASK[p] }; };

/** Apply the addressing plan (and bring interfaces up) on a router. */
export function address(r, { only } = {}) {
  for (const [n, a] of Object.entries(ADDR[r.id])) {
    if (only && !only.includes(n)) continue;
    const i = createInterface(r, n);
    Object.assign(i, { ip: ipm(a), shutdown: false, description: DESC[r.id]?.[n] || i.description || '' });
  }
  return r;
}

export function mkRouter(id, seq, { addressed = true, ports = 3 } = {}) {
  const r = router(id, { seq, ports: Array.from({ length: ports }, (_, i) => `GigabitEthernet0/${i}`) });
  if (addressed) address(r);
  return r;
}

/** ISP: not managed by the learner; preconfigured for BGP or a static return route. */
export function isp({ bgp = false } = {}) {
  const ISP = mkRouter('ISP', 9, { ports: 2 });
  ISP.managed = false;
  if (bgp) ISP.bgp = { asn: '64500', routerId: '203.0.113.1', neighbors: { '198.51.100.2': { remoteAs: '65010', defaultOriginate: true, description: 'ROUTEZERO-DAVAO' } }, networks: [{ net: '203.0.113.0', mask: '255.255.255.0' }] };
  else ISP.routes.push({ net: '10.30.0.0', mask: '255.255.0.0', nh: '198.51.100.2' });
  return ISP;
}

const hostFor = (id, ip, gw, mac) => pc(id, { ip, mask: '255.255.255.0', gw, mac });

/**
 * Regional network. Options:
 *   addressed   routers already have the addressing plan (default true)
 *   backup      include the R1–R3 backup link (default true)
 *   ispBgp      ISP runs eBGP (true) or has a static route to 10.30.0.0/16 (false)
 *   dc          include the regional data-center server on R2 G0/3
 */
export function regional({ addressed = true, backup = true, ispBgp = false, dc = true } = {}) {
  const R1 = mkRouter('R1', 1, { addressed });
  const R2 = mkRouter('R2', 2, { addressed, ports: 4 });
  const R3 = mkRouter('R3', 3, { addressed });
  if (!backup) for (const r of [R1, R3]) { delete r.interfaces['GigabitEthernet0/2']; }
  const ISP = isp({ bgp: ispBgp });
  const PCA = hostFor('PC-A', '10.30.1.10', '10.30.1.1', '0050797a0110');
  const PCB = hostFor('PC-B', '10.30.2.10', '10.30.2.1', '0050797a0210');
  const WEB = server('WEB', { ip: '203.0.113.10', mask: '255.255.255.0', gw: '203.0.113.1', mac: '00155d71aa10', services: [{ proto: 'tcp', port: 443, name: 'https' }, { proto: 'tcp', port: 80, name: 'http' }], http: { title: 'Cloud portal', body: 'portal.example.net' } });
  const devs = [R1, R2, R3, ISP, PCA, PCB, WEB];
  const links = [
    link('R2', 'GigabitEthernet0/1', 'R1', 'GigabitEthernet0/0', { type: 'OM4 fiber', connector: 'LC' }),
    link('R2', 'GigabitEthernet0/2', 'R3', 'GigabitEthernet0/0', { type: 'OM4 fiber', connector: 'LC' }),
    link('R2', 'GigabitEthernet0/0', 'ISP', 'GigabitEthernet0/0', { type: 'Cat6 UTP (ISP handoff)' }),
    link('PC-A', 'FastEthernet0', 'R1', 'GigabitEthernet0/1'),
    link('PC-B', 'FastEthernet0', 'R3', 'GigabitEthernet0/1'),
    link('WEB', 'FastEthernet0', 'ISP', 'GigabitEthernet0/1'),
  ];
  if (backup) links.push(link('R1', 'GigabitEthernet0/2', 'R3', 'GigabitEthernet0/2', { type: 'Cat6 UTP' }));
  if (dc) {
    devs.push(server('SRV-DC', { ip: '10.30.10.10', mask: '255.255.255.0', gw: '10.30.10.1', mac: '00155d71dc10', services: [{ proto: 'tcp', port: 443, name: 'https' }, { proto: 'tcp', port: 445, name: 'smb' }], http: { title: 'Regional apps', body: 'SRV-DC' } }));
    links.push(link('SRV-DC', 'FastEthernet0', 'R2', 'GigabitEthernet0/3'));
  } else delete R2.interfaces['GigabitEthernet0/3'];
  return network(devs, links);
}

// ------------------------------------------------------------ routing presets (used as lab baselines)
export const S = (r, net, mask, nh, ad) => { r.routes.push({ net, mask, ...(nh === 'Null0' ? { ifc: 'Null0' } : { nh }), ...(ad ? { ad } : {}) }); };

/** Fully static design (the outcome of Mission 02). */
export function staticDesign(n) {
  const { R1, R2, R3 } = n.devices;
  for (const [net, m] of [['10.30.2.0', '255.255.255.0'], ['10.30.10.0', '255.255.255.0'], ['10.30.23.0', '255.255.255.252']]) S(R1, net, m, '10.30.12.1');
  S(R2, '10.30.1.0', '255.255.255.0', '10.30.12.2'); S(R2, '10.30.2.0', '255.255.255.0', '10.30.23.2');
  for (const [net, m] of [['10.30.1.0', '255.255.255.0'], ['10.30.10.0', '255.255.255.0'], ['10.30.12.0', '255.255.255.252']]) S(R3, net, m, '10.30.23.1');
  return n;
}
/** Default-route design (Mission 03 outcome). */
export function defaultDesign(n) {
  const { R1, R2, R3 } = n.devices;
  S(R1, '0.0.0.0', '0.0.0.0', '10.30.12.1'); S(R3, '0.0.0.0', '0.0.0.0', '10.30.23.1');
  S(R2, '10.30.1.0', '255.255.255.0', '10.30.12.2'); S(R2, '10.30.2.0', '255.255.255.0', '10.30.23.2');
  S(R2, '0.0.0.0', '0.0.0.0', '198.51.100.1');
  return n;
}
const area0 = (net, wild) => ({ net, wild, area: 0 });
export function ospfDesign(n, { routers = ['R1', 'R2', 'R3'] } = {}) {
  const P = { routerId: null, networks: [], passive: [], passiveDefault: false, notPassive: [], defaultOriginate: false };
  const cfg = {
    R1: { routerId: '1.1.1.1', networks: [area0('10.30.12.0', '0.0.0.3'), area0('10.30.13.0', '0.0.0.3'), area0('10.30.1.0', '0.0.0.255'), area0('1.1.1.1', '0.0.0.0')], passive: ['GigabitEthernet0/1'] },
    R2: { routerId: '2.2.2.2', networks: [area0('10.30.12.0', '0.0.0.3'), area0('10.30.23.0', '0.0.0.3'), area0('10.30.10.0', '0.0.0.255'), area0('2.2.2.2', '0.0.0.0')], passive: ['GigabitEthernet0/3'] },
    R3: { routerId: '3.3.3.3', networks: [area0('10.30.23.0', '0.0.0.3'), area0('10.30.13.0', '0.0.0.3'), area0('10.30.2.0', '0.0.0.255'), area0('3.3.3.3', '0.0.0.0')], passive: ['GigabitEthernet0/1'] },
  };
  for (const id of routers) n.devices[id].ospf = { 1: { ...P, ...cfg[id] } };
  return n;
}
export function eigrpDesign(n, { routers = ['R1', 'R2', 'R3'] } = {}) {
  const P = { passive: [], passiveDefault: false, notPassive: [], routerId: null, autoSummary: false };
  const w = (net, wild) => ({ net, wild });
  const cfg = {
    R1: { routerId: '1.1.1.1', networks: [w('10.30.12.0', '0.0.0.3'), w('10.30.13.0', '0.0.0.3'), w('10.30.1.0', '0.0.0.255')], passive: ['GigabitEthernet0/1'] },
    R2: { routerId: '2.2.2.2', networks: [w('10.30.12.0', '0.0.0.3'), w('10.30.23.0', '0.0.0.3'), w('10.30.10.0', '0.0.0.255')], passive: ['GigabitEthernet0/3'] },
    R3: { routerId: '3.3.3.3', networks: [w('10.30.23.0', '0.0.0.3'), w('10.30.13.0', '0.0.0.3'), w('10.30.2.0', '0.0.0.255')], passive: ['GigabitEthernet0/1'] },
  };
  for (const id of routers) n.devices[id].eigrp = { 100: { ...P, ...cfg[id] } };
  return n;
}

// ------------------------------------------------------------ eBGP lab (Mission 13)
//   ISP (AS 64500) ── 198.51.100.0/30 ── R2 RouteZero HQ (AS 65010) ── 192.0.2.0/30 ── RGN Regional partner (AS 65020)
export function ebgpLab({ configured = false } = {}) {
  const R2 = mkRouter('R2', 2, { ports: 4, addressed: false });
  address(R2, { only: ['GigabitEthernet0/0', 'GigabitEthernet0/3', 'Loopback0'] });
  const g2 = createInterface(R2, 'GigabitEthernet0/2');
  Object.assign(g2, { ip: { ip: '192.0.2.1', mask: '255.255.255.252' }, shutdown: false, description: 'RGN-PARTNER-AS65020' });
  delete R2.interfaces['GigabitEthernet0/1'];
  const RGN = mkRouter('RGN', 4, { addressed: false, ports: 2 });
  Object.assign(createInterface(RGN, 'GigabitEthernet0/0'), { ip: { ip: '192.0.2.2', mask: '255.255.255.252' }, shutdown: false, description: 'ROUTEZERO-HQ-AS65010' });
  Object.assign(createInterface(RGN, 'GigabitEthernet0/1'), { ip: { ip: '10.40.1.1', mask: '255.255.255.0' }, shutdown: false, description: 'RGN-LAN' });
  Object.assign(createInterface(RGN, 'Loopback0'), { ip: { ip: '4.4.4.4', mask: '255.255.255.255' }, shutdown: false });
  const ISP = isp({ bgp: true });
  S(R2, '10.30.0.0', '255.255.0.0', 'Null0');
  if (configured) {
    R2.bgp = { asn: '65010', routerId: '2.2.2.2', neighbors: { '198.51.100.1': { remoteAs: '64500' }, '192.0.2.2': { remoteAs: '65020' } }, networks: [{ net: '10.30.0.0', mask: '255.255.0.0' }] };
    RGN.bgp = { asn: '65020', routerId: '4.4.4.4', neighbors: { '192.0.2.1': { remoteAs: '65010' } }, networks: [{ net: '10.40.1.0', mask: '255.255.255.0' }] };
  }
  const SRV = server('SRV-DC', { ip: '10.30.10.10', mask: '255.255.255.0', gw: '10.30.10.1', mac: '00155d71dc10', services: [{ proto: 'tcp', port: 443, name: 'https' }] });
  const PCR = pc('PC-RGN', { ip: '10.40.1.10', mask: '255.255.255.0', gw: '10.40.1.1', mac: '0050797a4010' });
  const WEB = server('WEB', { ip: '203.0.113.10', mask: '255.255.255.0', gw: '203.0.113.1', mac: '00155d71aa10', services: [{ proto: 'tcp', port: 443, name: 'https' }] });
  return network([R2, RGN, ISP, SRV, PCR, WEB], [
    link('R2', 'GigabitEthernet0/0', 'ISP', 'GigabitEthernet0/0', { type: 'Cat6 UTP (ISP handoff)' }),
    link('R2', 'GigabitEthernet0/2', 'RGN', 'GigabitEthernet0/0', { type: 'OM4 fiber', connector: 'LC' }),
    link('SRV-DC', 'FastEthernet0', 'R2', 'GigabitEthernet0/3'),
    link('PC-RGN', 'FastEthernet0', 'RGN', 'GigabitEthernet0/1'),
    link('WEB', 'FastEthernet0', 'ISP', 'GigabitEthernet0/1'),
  ]);
}

// ------------------------------------------------------------ diagram layouts
const nodes = {
  ISP: { x: 500, y: 52, type: 'router', sub: 'ISP POP · AS 64500 · unmanaged' },
  WEB: { x: 820, y: 52, type: 'server', sub: '203.0.113.10 · cloud portal' },
  R2: { x: 500, y: 200, type: 'router', sub: 'HQ · Lo0 2.2.2.2' },
  'SRV-DC': { x: 820, y: 200, type: 'server', sub: 'Regional DC · 10.30.10.10' },
  R1: { x: 200, y: 340, type: 'router', sub: 'Branch A · Lo0 1.1.1.1' },
  R3: { x: 800, y: 340, type: 'router', sub: 'Branch B · Lo0 3.3.3.3' },
  'PC-A': { x: 200, y: 470, type: 'pc', sub: '10.30.1.10 · gw .1' },
  'PC-B': { x: 800, y: 470, type: 'pc', sub: '10.30.2.10 · gw .1' },
};
const linkLabels = {
  'R2:GigabitEthernet0/0': '.2', 'ISP:GigabitEthernet0/0': '.1', 'R2:GigabitEthernet0/1': '.1', 'R1:GigabitEthernet0/0': '.2',
  'R2:GigabitEthernet0/2': '.1', 'R3:GigabitEthernet0/0': '.2', 'R1:GigabitEthernet0/2': '.1', 'R3:GigabitEthernet0/2': '.2',
  'R1:GigabitEthernet0/1': '.1', 'R3:GigabitEthernet0/1': '.1', 'R2:GigabitEthernet0/3': '.1', 'ISP:GigabitEthernet0/1': '.1',
};
const LINKS = {
  isp: 'R2:GigabitEthernet0/0--ISP:GigabitEthernet0/0', a: 'R2:GigabitEthernet0/1--R1:GigabitEthernet0/0', b: 'R2:GigabitEthernet0/2--R3:GigabitEthernet0/0',
  bk: 'R1:GigabitEthernet0/2--R3:GigabitEthernet0/2', lanA: 'PC-A:FastEthernet0--R1:GigabitEthernet0/1', lanB: 'PC-B:FastEthernet0--R3:GigabitEthernet0/1',
  dc: 'SRV-DC:FastEthernet0--R2:GigabitEthernet0/3', web: 'WEB:FastEthernet0--ISP:GigabitEthernet0/1',
};
/**
 * Diagram for the regional network. `proto` labels each link with its subnet and
 * the routing relationship that runs over it in this lab.
 */
export function regionalLayout({ proto = {}, backup = true, dc = true, title } = {}) {
  const p = (k, txt) => (proto[k] ? `${txt} · ${proto[k]}` : txt);
  const n = { ...nodes };
  if (!dc) delete n['SRV-DC'];
  return {
    width: 1000, height: 530, title: title || 'RouteZero Southern Regional Network — Davao',
    nodes: n,
    zones: [
      { x: 360, y: 14, w: 600, h: 86, label: 'ISP POP · 203.0.113.0/24', cloud: true },
      { x: 360, y: 150, w: 600, h: 100, label: 'ROUTEZERO REGIONAL HQ · DAVAO' },
      { x: 60, y: 300, w: 280, h: 220, label: 'BRANCH OFFICE A · 10.30.1.0/24', cls: 'vlan10' },
      { x: 660, y: 300, w: 280, h: 220, label: 'BRANCH OFFICE B · 10.30.2.0/24', cls: 'vlan20' },
    ],
    linkLabels,
    linkNotes: {
      [LINKS.isp]: p('isp', '198.51.100.0/30'), [LINKS.a]: p('a', '10.30.12.0/30'), [LINKS.b]: p('b', '10.30.23.0/30'),
      ...(backup ? { [LINKS.bk]: p('bk', '10.30.13.0/30 backup') } : {}),
      ...(dc ? { [LINKS.dc]: p('dc', '10.30.10.0/24') } : {}),
    },
  };
}
export const ebgpLayout = {
  width: 1000, height: 430, title: 'eBGP — RouteZero HQ, ISP and regional partner',
  nodes: {
    ISP: { x: 170, y: 90, type: 'router', sub: 'AS 64500 · unmanaged' }, WEB: { x: 170, y: 300, type: 'server', sub: '203.0.113.10' },
    R2: { x: 500, y: 90, type: 'router', sub: 'RouteZero HQ · AS 65010' }, 'SRV-DC': { x: 500, y: 300, type: 'server', sub: '10.30.10.10' },
    RGN: { x: 830, y: 90, type: 'router', sub: 'Regional partner · AS 65020' }, 'PC-RGN': { x: 830, y: 300, type: 'pc', sub: '10.40.1.10' },
  },
  zones: [{ x: 40, y: 20, w: 260, h: 390, label: 'AS 64500', cloud: true }, { x: 370, y: 20, w: 260, h: 390, label: 'AS 65010 · ROUTEZERO' }, { x: 700, y: 20, w: 260, h: 390, label: 'AS 65020 · PARTNER' }],
  linkLabels: { 'R2:GigabitEthernet0/0': '.2', 'ISP:GigabitEthernet0/0': '.1', 'R2:GigabitEthernet0/2': '.1', 'RGN:GigabitEthernet0/0': '.2', 'R2:GigabitEthernet0/3': '10.30.10.1', 'RGN:GigabitEthernet0/1': '10.40.1.1', 'ISP:GigabitEthernet0/1': '.1' },
  linkNotes: { 'R2:GigabitEthernet0/0--ISP:GigabitEthernet0/0': '198.51.100.0/30 · eBGP', 'R2:GigabitEthernet0/2--RGN:GigabitEthernet0/0': '192.0.2.0/30 · eBGP' },
};
export { LINKS };
